/**
 * MindWorks ACL - AP Reconciliation Detail Page
 * 
 * Current: v3.10 (2026-10-13)
 * Summary: escapeHtml delegates to Utils.escapeHtml.
 * v3.9 (2026-10-12) - escapeHtml delegates to Utils.escapeHtml.
 * v3.8 (2026-10-12) - Reconcile confirm opens through mountModal (ap-confirm-reconcile).
 * v3.7 (2026-04-01) - Fix Reconcile in Fishbowl modal — create DOM element before MWACL.Modal.open.
 * v3.6 (2026-04-01) - CC detection, auto-format ref# CC-MMDD-INV, payment info panel.
 * v3.4 (2026-04-01) - Shared fmtVariance + CSS classes for accounting colors.
 * v3.3 (2026-04-01) - Date mm/dd/yyyy, billed cost auto-format on blur,
 *          variance colors (green neg / red pos / dash zero),
 *          difference summary colors + initial load fix.
 * 
 * JARED: waitAndInit, MODULE_VERSION, mw_acl-core-* classes, self-initializing
 * WEISSMAN: Parallel fetch of document + PO + adjustments via single API call
 * CONVERGENCE: Stacked panels on mobile, side-by-side on desktop
 */

(function() {
  'use strict';

  var MODULE_VERSION = 'v3.10'; // v3.10: escapeHtml aliases MWACL.Utils
  const escapeHtml = window.MWACL.Utils.escapeHtml;

  var initialized = false;
  var state = {
    docId: null,
    document: null,
    invoice: null,
    po: null,
    aiSuggestions: null,
    adjustmentTypes: [],
    paymentTerms: [],
    draft: null,
    adjustments: [],
    loading: true,
    saving: false,
    reconciling: false,
  };

  function getDocIdFromUrl() {
    var params = new URLSearchParams(window.location.search);
    return params.get('docId');
  }

  function init() {
    if (initialized) return;
    if (window.MWACLPageAccessDenied) return;

    var container = document.getElementById('mw-ap-recon-detail-app');
    if (container) container.style.display = 'block';

    state.docId = getDocIdFromUrl();
    if (!state.docId) {
      renderError('No document ID provided. Go back to the list and select a document.');
      return;
    }

    loadDetail();
    initialized = true;
  }

  function loadDetail() {
    state.loading = true;

    window.MWACL.API.call('ap_get_reconciliation_detail', { docId: state.docId })
      .then(function(response) {
        var data = response.data || response;
        state.document = data.document;
        state.invoice = data.invoice;
        state.po = data.po;
        state.aiSuggestions = data.aiSuggestions;
        state.adjustmentTypes = data.adjustmentTypes || [];
        state.paymentTerms = data.paymentTerms || [];
        state.draft = data.draft;

        if (state.draft) {
          state.adjustments = state.draft.adjustments || [];
        }

        state.loading = false;
        render();
      })
      .catch(function(err) {
        state.loading = false;
        console.error('[ap-recon-detail] Load error:', err);
        renderError('Failed to load detail: ' + (err.message || 'Unknown error'));
      });
  }

  function render() {
    renderHeader();
    renderContent();
    bindActions();
  }

  function renderHeader() {
    var doc = state.document;
    var title = document.getElementById('ap-detail-title');
    var subtitle = document.getElementById('ap-detail-subtitle');
    var badges = document.getElementById('ap-detail-badges');
    var actions = document.getElementById('ap-detail-actions');

    if (title) title.textContent = doc.fileName || 'Document';
    if (subtitle) subtitle.textContent = (doc.vendor || '') + (doc.invoiceNumber ? ' \u2014 Invoice #' + doc.invoiceNumber : '');

    if (badges) {
      var html = '';
      html += '<span class="mw_acl-core-badge mw_acl-core-badge-' + triageBadgeClass(doc.triageStatus) + '">' + triageLabel(doc.triageStatus) + '</span> ';
      if (doc.matchedPO) {
        html += '<span class="mw_acl-core-badge mw_acl-core-badge-active">PO ' + escapeHtml(doc.matchedPO) + '</span> ';
      }
      if (doc.confidence) {
        html += '<span class="mw_acl-core-badge" style="background:#f3f4f6;color:#374151;">' + Math.round(doc.confidence * 100) + '% confidence</span>';
      }
      badges.innerHTML = html;
    }

    if (actions) {
      var isReconciled = doc.reconStatus === 'reconciled' || doc.reconStatus === 'reconciled_synced';
      if (isReconciled) {
        actions.innerHTML = '<span class="mw_acl-core-badge mw_acl-core-badge-active" style="padding:8px 16px;font-size:14px;">Reconciled</span>';
      } else {
        actions.innerHTML =
          '<button class="mw_acl-core-btn mw_acl-core-btn-secondary" id="ap-save-draft-btn">Save Draft</button> ' +
          '<button class="mw_acl-core-btn mw_acl-core-btn-primary" id="ap-reconcile-btn" style="background:#059669;">Reconcile in Fishbowl</button>';
      }
    }
  }

  function renderContent() {
    var container = document.getElementById('ap-detail-content');
    if (!container) return;

    var html = '';
    html += renderPanels();
    html += renderAISuggestions();
    html += renderReconDetails();
    html += renderLineGrid();
    html += renderAdjustments();
    html += renderSummary();

    container.innerHTML = html;
  }

  function renderPanels() {
    var inv = state.invoice;
    var po = state.po;

    var html = '<div class="ap-detail-panels">';

    html += '<div class="mw_acl-core-section">';
    html += '<div class="mw_acl-core-section-header"><h2 class="mw_acl-core-section-title">Invoice (Extracted)</h2>' +
            '<span class="ap-detail-tag ap-detail-tag-invoice">From scan</span></div>';
    html += '<div class="ap-detail-panel-body"><div class="ap-detail-field-grid">';
    html += field('Vendor', inv.vendor);
    html += field('Invoice #', inv.invoiceNumber);
    html += field('Date', fmtDate(inv.invoiceDate));
    html += field('Due Date', fmtDate(inv.dueDate));
    html += field('Terms', inv.terms);
    html += field('Subtotal', money(inv.subtotal));
    html += field('Tax', money(inv.tax));
    html += field('Total', money(inv.total), true);
    html += '</div>';

    var doc = state.document;
    if (doc.cardLastFour) {
      html += '<div class="ap-detail-payment-info">';
      var method = (doc.paymentMethod || 'card').charAt(0).toUpperCase() + (doc.paymentMethod || 'card').slice(1);
      var cardLabel = method + ' ...' + escapeHtml(doc.cardLastFour);
      if (doc.ccAccount) cardLabel += ' (' + escapeHtml(doc.ccAccount);
      if (doc.employeeName) cardLabel += ' \u2014 ' + escapeHtml(doc.employeeName);
      if (doc.ccAccount) cardLabel += ')';
      html += '<div class="ap-detail-field"><label>Payment</label><div class="ap-detail-field-val">' + cardLabel + '</div></div>';
      if (doc.chargeDate) {
        var srcLabel = doc.chargeDateSource === 'explicit' ? '' : ' <span style="color:#6b7280;font-size:12px;">(from ' + escapeHtml(doc.chargeDateSource || 'inferred') + ')</span>';
        html += '<div class="ap-detail-field"><label>Charge Date</label><div class="ap-detail-field-val">' + fmtDate(doc.chargeDate) + srcLabel + '</div></div>';
      }
      if (doc.shipDate) {
        html += '<div class="ap-detail-field"><label>Ship Date</label><div class="ap-detail-field-val">' + fmtDate(doc.shipDate) + '</div></div>';
      }
      if (doc.authorizationCode) {
        html += '<div class="ap-detail-field"><label>Auth Code</label><div class="ap-detail-field-val">' + escapeHtml(doc.authorizationCode) + '</div></div>';
      }
      if (doc.internalContact && doc.internalContact.name) {
        var contactStr = escapeHtml(doc.internalContact.name);
        if (doc.internalContact.phone) contactStr += ' ' + escapeHtml(doc.internalContact.phone);
        html += '<div class="ap-detail-field"><label>Ordered By</label><div class="ap-detail-field-val">' + contactStr + '</div></div>';
      }
      html += '</div>';
    }

    html += '</div></div>';

    html += '<div class="mw_acl-core-section">';
    if (po) {
      html += '<div class="mw_acl-core-section-header"><h2 class="mw_acl-core-section-title">PO ' + escapeHtml(po.num) + ' (Fishbowl)</h2>' +
              '<span class="ap-detail-tag ap-detail-tag-po">Live data</span></div>';
      html += '<div class="ap-detail-panel-body"><div class="ap-detail-field-grid">';
      html += field('Vendor', po.vendorName);
      html += field('PO #', po.num);
      html += field('Status', '<span class="mw_acl-core-badge mw_acl-core-badge-active">' + escapeHtml(po.statusLabel) + '</span>');
      html += field('Date Issued', fmtDate(po.dateIssued));
      html += field('PO Total', money(po.total));
      html += field('Received', money(po.receivedTotal));
      html += field('Remaining', money(po.remainingTotal));
      html += '</div></div>';
    } else {
      html += '<div class="mw_acl-core-section-header"><h2 class="mw_acl-core-section-title">PO (Fishbowl)</h2></div>';
      html += '<div class="ap-detail-panel-body"><p style="color:#6b7280;">No matched PO data available.</p></div>';
    }
    html += '</div>';

    html += '</div>';
    return html;
  }

  function renderAISuggestions() {
    var ai = state.aiSuggestions;
    if (!ai || (!ai.flags.length && !ai.summary)) return '';

    var html = '<div class="ap-detail-ai-banner">';
    html += '<h3 class="ap-detail-ai-title">' +
            (window.MWACL && window.MWACL.Icons ? window.MWACL.Icons.get('sparkles', 16) : '\uD83E\uDD16') +
            ' AI Reconciliation Suggestions</h3>';
    html += '<p style="font-size:13px;color:#4338ca;margin-bottom:8px;">' + escapeHtml(ai.summary) + '</p>';

    if (ai.flags && ai.flags.length > 0) {
      html += '<ul class="ap-detail-ai-flags">';
      ai.flags.forEach(function(flag) {
        var cls = flag.type === 'warning' ? 'ap-detail-flag-warning' : 'ap-detail-flag-info';
        html += '<li class="' + cls + '">' + escapeHtml(flag.message) + '</li>';
      });
      html += '</ul>';
    }

    html += '</div>';
    return html;
  }

  function renderLineGrid() {
    var po = state.po;
    if (!po || !po.receivingLines || po.receivingLines.length === 0) return '';

    var ai = state.aiSuggestions;
    var matchMap = {};
    if (ai && ai.lineMatches) {
      ai.lineMatches.forEach(function(m) { matchMap[m.receiptItemId] = m; });
    }

    var isReconciled = state.document.reconStatus === 'reconciled' || state.document.reconStatus === 'reconciled_synced';
    var reconcilableLines = po.receivingLines.filter(function(l) { return l.reconcilable; });
    var matchedCount = 0;
    reconcilableLines.forEach(function(l) { if (matchMap[l.receiptItemId] && matchMap[l.receiptItemId].matched) matchedCount++; });

    var html = '<div class="mw_acl-core-section">';
    html += '<div class="mw_acl-core-section-header"><h2 class="mw_acl-core-section-title">Line Matching (Receiving Lines)</h2>' +
            '<span style="font-size:13px;color:#6b7280;">' + matchedCount + ' of ' + reconcilableLines.length + ' AI-matched</span></div>';

    html += '<div class="mw_acl-core-table-container"><table class="mw_acl-core-table">';
    html += '<thead><tr>';
    if (!isReconciled) {
      html += '<th class="ap-detail-check-col"><input type="checkbox" class="ap-detail-select-all" id="ap-select-all" checked title="Select / Deselect All"></th>';
    }
    html += '<th>Line</th><th>Part #</th><th>Description</th>';
    html += '<th style="text-align:right;">Qty</th><th>Received</th><th style="text-align:right;">PO Cost</th>';
    html += '<th style="text-align:right;">Billed Cost</th><th style="text-align:right;">Variance</th>';
    html += '</tr></thead><tbody>';

    po.receivingLines.forEach(function(line) {
      var match = matchMap[line.receiptItemId];
      var isMatched = match && match.matched;
      var suggested = isMatched ? match.suggestedBilledCost : line.poCost;
      var draftLine = state.draft && state.draft.lines
        ? state.draft.lines.find(function(dl) { return dl.receiptItemId === line.receiptItemId; })
        : null;
      var billedCost = draftLine ? draftLine.billedCost : suggested;
      var variance = billedCost - line.poCost;

      html += '<tr class="' + (line.alreadyReconciled ? 'ap-detail-line-reconciled' : '') + '">';

      if (!isReconciled) {
        html += '<td class="ap-detail-check-col"><input type="checkbox" class="ap-detail-line-check" data-receipt-item-id="' + line.receiptItemId + '"' +
                (line.reconcilable ? ' checked' : ' disabled') +
                (line.alreadyReconciled ? ' disabled title="Already reconciled"' : '') + '></td>';
      }

      var matchDot = isMatched
        ? '<span class="ap-detail-match-indicator matched" title="AI matched"></span>'
        : (line.reconcilable ? '<span class="ap-detail-match-indicator unmatched" title="Unmatched"></span>' : '');
      html += '<td data-label="Line">' + matchDot + line.poLineItem + '</td>';
      html += '<td data-label="Part #"><code>' + escapeHtml(line.partNum) + '</code></td>';
      html += '<td data-label="Description">' + escapeHtml(truncate(line.description, 40)) + '</td>';
      html += '<td data-label="Qty" style="text-align:right;">' + line.qty + '</td>';
      html += '<td data-label="Received">' + fmtDate(line.dateReceived) + '</td>';
      html += '<td data-label="PO Cost" style="text-align:right;">' + money(line.poCost) + '</td>';

      if (isReconciled || line.alreadyReconciled) {
        html += '<td data-label="Billed" style="text-align:right;">' + money(billedCost) + '</td>';
      } else {
        html += '<td data-label="Billed" style="text-align:right;">' +
                '<input type="text" class="ap-detail-cost-input" data-receipt-item-id="' + line.receiptItemId + '" value="' + billedCost.toFixed(2) + '"></td>';
      }

      html += '<td data-label="Variance" style="text-align:right;">' +
              (window.MWACL && window.MWACL.Utils ? window.MWACL.Utils.fmtVariance(variance) : money(variance)) + '</td>';
      html += '</tr>';
    });

    html += '</tbody></table></div></div>';
    return html;
  }

  function renderAdjustments() {
    var isReconciled = state.document.reconStatus === 'reconciled' || state.document.reconStatus === 'reconciled_synced';

    var html = '<div class="mw_acl-core-section">';
    html += '<div class="mw_acl-core-section-header"><h2 class="mw_acl-core-section-title">Adjustments</h2>' +
            '<span style="font-size:13px;color:#6b7280;">Shipping, tax, COGS adjustments</span></div>';
    html += '<div id="ap-detail-adj-list">';

    var adjs = state.adjustments.length > 0 ? state.adjustments : (state.draft && state.draft.adjustments ? state.draft.adjustments : []);
    adjs.forEach(function(adj, idx) {
      html += renderAdjRow(adj, idx, isReconciled);
    });

    html += '</div>';

    if (!isReconciled) {
      html += '<div style="padding:10px 14px;">' +
              '<button class="mw_acl-core-btn mw_acl-core-btn-sm mw_acl-core-btn-secondary" id="ap-add-adj-btn" style="border-style:dashed;">+ Add Adjustment</button></div>';
    }

    html += '</div>';
    return html;
  }

  function renderAdjRow(adj, idx, readonly) {
    var html = '<div class="ap-detail-adj-row" data-adj-idx="' + idx + '">';

    if (readonly) {
      html += '<span style="flex:1;">' + escapeHtml(adj.description || adj.partNum || 'Adjustment') + '</span>';
      html += '<span>' + money(adj.amount) + '</span>';
    } else {
      html += '<select class="mw_acl-core-form-select ap-detail-adj-select" data-adj-idx="' + idx + '" style="flex:1;max-width:260px;">';
      state.adjustmentTypes.forEach(function(t) {
        var selected = t.partId === adj.partId ? ' selected' : '';
        html += '<option value="' + t.partId + '"' + selected + '>' + escapeHtml(t.description) + ' (' + escapeHtml(t.partNum) + ')</option>';
      });
      html += '</select>';
      html += '<input type="text" class="ap-detail-cost-input ap-detail-adj-amount" data-adj-idx="' + idx + '" value="' + (parseFloat(adj.amount) || 0).toFixed(2) + '">';
      html += '<button class="mw_acl-core-btn mw_acl-core-btn-sm mw_acl-core-btn-danger ap-detail-adj-remove" data-adj-idx="' + idx + '" title="Remove">&times;</button>';
    }

    html += '</div>';
    return html;
  }

  function renderReconDetails() {
    var isReconciled = state.document.reconStatus === 'reconciled' || state.document.reconStatus === 'reconciled_synced';
    var draft = state.draft || {};
    var doc = state.document;

    var refNo = draft.refNo || '';
    if (!refNo && doc.ccAccount && doc.chargeDate) {
      refNo = formatCCRefNo(doc.ccAccount, doc.chargeDate, state.invoice.invoiceNumber);
    }
    if (!refNo) refNo = state.invoice.invoiceNumber || '';

    var billDate = draft.billDate || state.invoice.invoiceDate || new Date().toISOString().split('T')[0];

    var html = '<div class="mw_acl-core-section">';
    html += '<div class="mw_acl-core-section-header"><h2 class="mw_acl-core-section-title">Reconciliation Details</h2></div>';
    html += '<div class="ap-detail-panel-body"><div class="ap-detail-field-grid">';

    if (isReconciled) {
      html += field('Ref No', refNo);
      html += field('Bill Date', fmtDate(billDate));
      html += field('Terms', state.po && state.po.termsName ? state.po.termsName : '-');
      html += field('Reconciled By', doc.reconciledBy || '-');
      html += field('Reconciled At', fmtDate(doc.reconciledAt));
    } else {
      var refLabel = doc.ccAccount ? 'Ref No (CC-Date-Invoice)' : 'Ref No (Vendor Invoice #)';
      html += '<div class="ap-detail-field"><label>' + refLabel + '</label>' +
              '<input type="text" class="ap-detail-text-input" id="ap-refno-input" value="' + escapeHtml(refNo) + '" maxlength="20">';
      if (doc.ccAccount && doc.employeeName) {
        html += '<div class="ap-detail-cc-hint">' + escapeHtml(doc.ccAccount) +
                ' (' + escapeHtml(doc.employeeName) + ')' +
                (doc.chargeDate ? ' \u2014 charged ' + fmtDate(doc.chargeDate) : '') + '</div>';
      }
      html += '</div>';
      html += '<div class="ap-detail-field"><label>Bill Date</label>' +
              '<input type="date" class="ap-detail-text-input" id="ap-billdate-input" value="' + escapeHtml(billDate) + '"></div>';

      if (state.paymentTerms.length > 0) {
        var currentTermsId = state.po ? state.po.paymentTermsId : null;
        html += '<div class="ap-detail-field"><label>Payment Terms</label>' +
                '<select class="mw_acl-core-form-select ap-detail-text-input" id="ap-terms-select">';
        state.paymentTerms.forEach(function(t) {
          var sel = t.id === currentTermsId ? ' selected' : '';
          html += '<option value="' + t.id + '"' + sel + '>' + escapeHtml(t.name) + '</option>';
        });
        html += '</select></div>';
      }
    }

    html += '</div></div></div>';
    return html;
  }

  function renderSummary() {
    var po = state.po;
    var poTotal = po ? po.total : 0;
    var billedLines = getCheckedLineTotals();
    var adjTotal = getAdjustmentTotal();
    var invoiceTotal = parseFloat(state.invoice.total) || 0;
    var difference = (billedLines + adjTotal) - invoiceTotal;

    var html = '<div class="ap-detail-summary">';
    html += '<div class="ap-detail-summary-numbers">';
    html += summaryItem('PO Total', money(poTotal));
    html += '<div class="ap-detail-summary-item"><label>Billed Lines</label><div class="ap-detail-summary-val" id="ap-summary-billed">' + money(billedLines) + '</div></div>';
    html += '<div class="ap-detail-summary-item"><label>Adjustments</label><div class="ap-detail-summary-val" id="ap-summary-adj">' + money(adjTotal) + '</div></div>';
    html += summaryItem('Invoice Total', money(invoiceTotal), 'large');

    html += '<div class="ap-detail-summary-item"><label>Difference</label><div class="ap-detail-summary-val ' + summaryDiffClass(difference) + '" id="ap-summary-diff" style="font-weight:700;">' +
            summaryDiffText(difference) + '</div></div>';
    html += '</div>';

    var isReconciled = state.document.reconStatus === 'reconciled' || state.document.reconStatus === 'reconciled_synced';
    if (!isReconciled) {
      html += '<div class="ap-detail-summary-actions">' +
              '<button class="mw_acl-core-btn mw_acl-core-btn-secondary" id="ap-summary-cancel">Cancel</button> ' +
              '<button class="mw_acl-core-btn mw_acl-core-btn-secondary" id="ap-summary-draft">Save Draft</button> ' +
              '<button class="mw_acl-core-btn" id="ap-summary-reconcile" style="background:#059669;color:#fff;">Reconcile in Fishbowl</button>' +
              '</div>';
    }

    html += '</div>';
    return html;
  }

  function bindActions() {
    var saveDraftBtn = document.getElementById('ap-save-draft-btn');
    var reconcileBtn = document.getElementById('ap-reconcile-btn');
    var summaryCancel = document.getElementById('ap-summary-cancel');
    var summaryDraft = document.getElementById('ap-summary-draft');
    var summaryRecon = document.getElementById('ap-summary-reconcile');
    var addAdjBtn = document.getElementById('ap-add-adj-btn');

    if (saveDraftBtn) saveDraftBtn.addEventListener('click', handleSaveDraft);
    if (reconcileBtn) reconcileBtn.addEventListener('click', handleReconcile);
    if (summaryCancel) summaryCancel.addEventListener('click', function() { window.location.href = '/pages/acl-ap-reconciliation'; });
    if (summaryDraft) summaryDraft.addEventListener('click', handleSaveDraft);
    if (summaryRecon) summaryRecon.addEventListener('click', handleReconcile);
    if (addAdjBtn) addAdjBtn.addEventListener('click', handleAddAdjustment);

    document.querySelectorAll('.ap-detail-adj-remove').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var idx = parseInt(btn.getAttribute('data-adj-idx'));
        state.adjustments.splice(idx, 1);
        render();
      });
    });

    var selectAll = document.getElementById('ap-select-all');
    if (selectAll) {
      selectAll.addEventListener('change', function() {
        var checked = selectAll.checked;
        selectAll.indeterminate = false;
        document.querySelectorAll('.ap-detail-line-check:not(:disabled)').forEach(function(cb) {
          cb.checked = checked;
        });
        updateSummaryTotals();
      });
    }

    document.querySelectorAll('.ap-detail-line-check').forEach(function(cb) {
      cb.addEventListener('change', function() {
        var allChecks = document.querySelectorAll('.ap-detail-line-check:not(:disabled)');
        var checkedChecks = document.querySelectorAll('.ap-detail-line-check:not(:disabled):checked');
        if (selectAll) {
          selectAll.checked = checkedChecks.length > 0 && checkedChecks.length === allChecks.length;
          selectAll.indeterminate = checkedChecks.length > 0 && checkedChecks.length < allChecks.length;
        }
        updateSummaryTotals();
      });
    });
    document.querySelectorAll('.ap-detail-cost-input').forEach(function(input) {
      input.addEventListener('input', updateSummaryTotals);
      input.addEventListener('blur', function() {
        var n = parseFloat(input.value);
        if (!isNaN(n)) input.value = n.toFixed(2);
      });
    });
    document.querySelectorAll('.ap-detail-adj-amount').forEach(function(input) {
      input.addEventListener('input', updateSummaryTotals);
    });
  }

  function updateSummaryTotals() {
    var billedLines = getCheckedLineTotals();
    var adjTotal = getAdjustmentTotal();
    var invoiceTotal = parseFloat(state.invoice.total) || 0;
    var difference = (billedLines + adjTotal) - invoiceTotal;
    var poTotal = state.po ? state.po.total : 0;

    var billedEl = document.getElementById('ap-summary-billed');
    var adjEl = document.getElementById('ap-summary-adj');
    var diffEl = document.getElementById('ap-summary-diff');

    if (billedEl) billedEl.textContent = money(billedLines);
    if (adjEl) adjEl.textContent = money(adjTotal);
    if (diffEl) {
      diffEl.className = 'ap-detail-summary-val ' + summaryDiffClass(difference);
      diffEl.style.fontWeight = '700';
      diffEl.textContent = summaryDiffText(difference);
    }
  }

  function handleSaveDraft() {
    if (state.saving) return;
    state.saving = true;
    announce('Saving draft...');

    var payload = collectFormData();

    window.MWACL.API.call('ap_save_recon_draft', {
      docId: state.docId,
      draft: payload,
    })
    .then(function() {
      announce('Draft saved');
      state.saving = false;
    })
    .catch(function(err) {
      announce('Save failed: ' + err.message);
      state.saving = false;
    });
  }

  function handleReconcile() {
    if (state.reconciling) return;
    if (!window.MWACL || !window.MWACL.Utils || typeof window.MWACL.Utils.mountModal !== 'function') {
      notifyError('Modal helper not loaded');
      return;
    }
    var modalId = 'ap-confirm-reconcile';
    var el = window.MWACL.Utils.mountModal(modalId, {
      title: 'Reconcile in Fishbowl',
      maxWidth: '400px',
      draggable: false,
      resizable: false,
      bodyHtml:
        '<p>This will create receipt and posting records in Fishbowl. This action cannot be undone from Fox Den.</p>' +
        '<p>Continue?</p>',
      footerHtml:
        window.MWACL.Utils.modalButton('Cancel', null, 'secondary', true) +
        window.MWACL.Utils.modalButton('Reconcile', 'confirm', 'primary')
    });
    window.MWACL.Utils.wireModalAction(el, 'confirm', function() {
      window.MWACL.Modal.close(modalId);
      executeReconciliation();
    });
  }

  function executeReconciliation() {
    state.reconciling = true;
    announce('Reconciling...');

    var payload = collectFormData();

    var termsSelect = document.getElementById('ap-terms-select');
    var termsPromise = Promise.resolve();
    if (termsSelect) {
      var selectedTermsId = parseInt(termsSelect.value);
      var currentTermsId = state.po ? state.po.paymentTermsId : null;
      if (selectedTermsId && selectedTermsId !== currentTermsId) {
        announce('Updating PO terms...');
        termsPromise = window.MWACL.API.call('ap_update_po_terms', {
          docId: state.docId,
          termsId: selectedTermsId,
        });
      }
    }

    termsPromise
    .then(function() {
      announce('Reconciling in Fishbowl...');
      return window.MWACL.API.call('ap_execute_reconciliation', {
        docId: state.docId,
        refNo: payload.refNo,
        billDate: payload.billDate,
        lines: payload.lines,
        adjustments: payload.adjustments,
      });
    })
    .then(function(response) {
      var data = response.data || response;
      announce('Reconciliation complete');
      state.reconciling = false;

      state.document.reconStatus = 'reconciled';
      state.document.triageStatus = 'reconciled';
      state.document.reconciledAt = data.reconciledAt;
      state.document.reconciledTotal = data.reconciledTotal;
      render();
    })
    .catch(function(err) {
      notifyError('Reconciliation failed: ' + err.message);
      state.reconciling = false;
    });
  }

  function handleAddAdjustment() {
    if (state.adjustmentTypes.length === 0) {
      announce('No adjustment types available');
      return;
    }
    var first = state.adjustmentTypes[0];
    state.adjustments.push({
      partId: first.partId,
      partNum: first.partNum,
      description: first.description,
      amount: 0,
    });
    render();
  }

  function collectFormData() {
    var refNo = '';
    var billDate = '';
    var refInput = document.getElementById('ap-refno-input');
    var dateInput = document.getElementById('ap-billdate-input');
    if (refInput) refNo = refInput.value;
    if (dateInput) billDate = dateInput.value;

    var lines = [];
    document.querySelectorAll('.ap-detail-line-check:checked:not(:disabled)').forEach(function(cb) {
      var riId = parseInt(cb.getAttribute('data-receipt-item-id'));
      var costInput = document.querySelector('.ap-detail-cost-input[data-receipt-item-id="' + riId + '"]');
      var billedCost = costInput ? parseFloat(costInput.value) || 0 : 0;
      lines.push({ receiptItemId: riId, billedCost: billedCost });
    });

    var adjustments = [];
    document.querySelectorAll('.ap-detail-adj-row').forEach(function(row) {
      var idx = parseInt(row.getAttribute('data-adj-idx'));
      var select = row.querySelector('.ap-detail-adj-select');
      var amountInput = row.querySelector('.ap-detail-adj-amount');
      if (select && amountInput) {
        adjustments.push({
          partId: parseInt(select.value),
          amount: parseFloat(amountInput.value) || 0,
        });
      }
    });

    return { refNo: refNo, billDate: billDate, lines: lines, adjustments: adjustments };
  }

  function getCheckedLineTotals() {
    var total = 0;
    var inputs = document.querySelectorAll('.ap-detail-cost-input[data-receipt-item-id]');

    if (inputs.length > 0) {
      inputs.forEach(function(input) {
        var cb = document.querySelector('.ap-detail-line-check[data-receipt-item-id="' + input.getAttribute('data-receipt-item-id') + '"]');
        if (cb && cb.checked) {
          total += parseFloat(input.value) || 0;
        }
      });
      return total;
    }

    return computeBilledFromState();
  }

  function computeBilledFromState() {
    var total = 0;
    if (!state.po || !state.po.receivingLines) return total;

    var matchMap = {};
    if (state.aiSuggestions && state.aiSuggestions.lineMatches) {
      state.aiSuggestions.lineMatches.forEach(function(m) { matchMap[m.receiptItemId] = m; });
    }

    state.po.receivingLines.forEach(function(line) {
      if (!line.reconcilable) return;
      var match = matchMap[line.receiptItemId];
      var isMatched = match && match.matched;
      var suggested = isMatched ? match.suggestedBilledCost : line.poCost;
      var draftLine = state.draft && state.draft.lines
        ? state.draft.lines.find(function(dl) { return dl.receiptItemId === line.receiptItemId; })
        : null;
      total += draftLine ? draftLine.billedCost : suggested;
    });

    return total;
  }

  function getAdjustmentTotal() {
    var total = 0;
    var inputs = document.querySelectorAll('.ap-detail-adj-amount');
    if (inputs.length > 0) {
      inputs.forEach(function(input) {
        total += parseFloat(input.value) || 0;
      });
      return total;
    }
    state.adjustments.forEach(function(a) { total += parseFloat(a.amount) || 0; });
    return total;
  }

  function field(label, value, bold) {
    return '<div class="ap-detail-field"><label>' + label + '</label>' +
           '<div class="ap-detail-field-val' + (bold ? ' ap-detail-field-bold' : '') + '">' + (value || '-') + '</div></div>';
  }

  function summaryItem(label, value, size) {
    return '<div class="ap-detail-summary-item"><label>' + label + '</label>' +
           '<div class="ap-detail-summary-val' + (size === 'large' ? ' large' : '') + '">' + value + '</div></div>';
  }

  function money(val) {
    if (val === null || val === undefined) return '-';
    var n = parseFloat(val);
    if (isNaN(n)) return '-';
    return '$' + n.toFixed(2);
  }

  function formatCCRefNo(ccAccount, chargeDate, invoiceNumber) {
    if (!ccAccount || !chargeDate) return (invoiceNumber || '').substring(0, 20);
    var mm = chargeDate.substring(5, 7);
    var dd = chargeDate.substring(8, 10);
    var prefix = ccAccount + '-' + mm + dd + '-';
    var maxLen = 20 - prefix.length;
    var inv = invoiceNumber || '';
    if (inv.length > maxLen) inv = inv.substring(inv.length - maxLen);
    return prefix + inv;
  }

  function fmtDate(val) {
    if (!val) return '-';
    if (val._seconds !== undefined) {
      var ts = new Date(val._seconds * 1000);
      return String(ts.getUTCMonth() + 1).padStart(2, '0') + '/' +
             String(ts.getUTCDate()).padStart(2, '0') + '/' + ts.getUTCFullYear();
    }
    var s = String(val);
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
    var iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return iso[2] + '/' + iso[3] + '/' + iso[1];
    var d = new Date(val);
    if (isNaN(d.getTime())) return '-';
    return String(d.getMonth() + 1).padStart(2, '0') + '/' +
           String(d.getDate()).padStart(2, '0') + '/' + d.getFullYear();
  }

  function summaryDiffClass(val) {
    return Math.abs(val) < 0.005 || val <= 0 ? 'mw_acl-core-amount-favorable' : 'mw_acl-core-amount-unfavorable';
  }

  function summaryDiffText(val) {
    if (Math.abs(val) < 0.005) return '$0.00';
    return (val >= 0 ? '+' : '-') + '$' + Math.abs(val).toFixed(2);
  }

  function triageBadgeClass(status) {
    var map = { ready: 'active', needs_review: 'invited', reconciled: 'active', not_actionable: 'inactive' };
    return map[status] || 'inactive';
  }

  function triageLabel(status) {
    var map = { ready: 'Ready to Reconcile', needs_review: 'Needs Review', reconciled: 'Reconciled', not_actionable: 'Not Actionable' };
    return map[status] || status || '-';
  }

  function truncate(str, max) {
    if (!str) return '';
    return str.length > max ? str.substring(0, max - 2) + '...' : str;
  }


  function announce(msg) {
    var live = document.getElementById('ap-detail-live');
    if (live) live.textContent = msg;
  }

  function notifyError(msg) {
    announce(msg);
    if (window.MWACL && window.MWACL.Utils && typeof window.MWACL.Utils.showToast === 'function') {
      window.MWACL.Utils.showToast(msg, 'error');
    }
  }

  function renderError(msg) {
    var container = document.getElementById('ap-detail-content');
    if (container) {
      container.innerHTML = '<div style="text-align:center;padding:3rem;color:#dc2626;">' +
        '<p>' + escapeHtml(msg) + '</p>' +
        '<a href="/pages/acl-ap-reconciliation" class="mw_acl-core-btn mw_acl-core-btn-secondary" style="margin-top:1rem;display:inline-block;">Back to List</a></div>';
    }
  }

  async function waitAndInit() {
    if (window.MWACL && window.MWACL.Utils && window.MWACL.Utils.waitForAuth) {
      await window.MWACL.Utils.waitForAuth();
    }
    await new Promise(function(r) { setTimeout(r, 300); });
    init();
  }

  window.MWACLAPReconDetail = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitAndInit);
  } else {
    waitAndInit();
  }

  console.log('[mw_acl-ap-reconciliation-detail.js] Module loaded - ' + MODULE_VERSION);
})();
